import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useLocalSearchParams } from 'expo-router';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { Text as RNText } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { AVATAR_SLOT_SIZE, getGroupedAvatarLayout } from '@/components/inbox/MessageCard';
import { QuestionGroupHoverCard } from '@/components/inbox/QuestionGroupHoverCard';
import { QuestionGroupTip } from '@/components/inbox/QuestionGroupTip';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useOpenInquiryChat } from '@/hooks/inbox/useOpenInquiryChat';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { useInboxQuestionGroupCardsStore } from '@/services/stores/inboxQuestionGroupCardsStore';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import {
  buildQuestionGroupHeading,
  buildQuestionGroupTitle,
  getQuestionGroupCaseLine,
  formatQuestionGroupNames,
  getQuestionGroupBadge,
  getQuestionGroupConversationIds,
  getQuestionGroupOpenTarget,
  getQuestionGroupRows,
  getUnreadSubmitterThreads,
  getRowPreview,
  isThreadWaiting,
  getThreadStatus,
  summarizeQuestionGroup,
  type QuestionGroupBadge,
  type QuestionGroup,
  type QuestionGroupRow,
} from '@/util/questionGroups';

/** The inquiry group card's avatar slot, sized to sit beside its two text rows. */
const AVATAR_SLOT = 40;

const SUBMITTER_AVATAR_SIZE = 36;

/** Line 2 of a sent thread the expert has not answered. */
const NO_REPLY_LABEL = 'Awaiting response';

/** Line 2 of a picked expert's row before staff send the question on to them. */
const NOT_RELAYED_LABEL = 'Not sent yet';

/** Line 2 for a group with no expert yet, so the header never shows a blank line. */
const NO_EXPERTS_LABEL = 'No experts yet';

const CLUSTER_SCALE = AVATAR_SLOT / AVATAR_SLOT_SIZE;

/**
 * Where an expert's avatar sits in the cluster: the app's own arrangement, one on top and two
 * below for three, a diagonal for two, one circle for one, drawn at the inquiry group card's scale.
 */
function getClusterLayout(index: number, total: number) {
  if (total === 1) {
    return { left: 0, top: 0, size: SUBMITTER_AVATAR_SIZE };
  }

  const layout = getGroupedAvatarLayout(index, total);

  return {
    left: Math.round(layout.left * CLUSTER_SCALE),
    top: Math.round(layout.top * CLUSTER_SCALE),
    size: Math.round(layout.size * CLUSTER_SCALE),
  };
}

/**
 * Who Ask Anonymously is talking to in this inquiry group. Once staff send the question to
 * experts, those are the conversations, so the card shows up to three of their faces. Before that,
 * every thread is the submitter's with Ask Anonymously, so the card shows the submitter alone with
 * a count of those threads. The count is drawn, not spoken: the card's accessible name already says
 * how many experts there are.
 */
function QuestionGroupAvatars({
  rows,
  submitter,
}: {
  rows: QuestionGroupRow[];
  submitter: InquiryV2['author'] | null | undefined;
}) {
  const sentRows = rows.filter((row) => !row.fromSubmitter).slice(0, 3);

  const submitterThreadCount = rows.filter((row) => row.fromSubmitter).length;

  if (!sentRows.length) {
    const uri = getAvatarUrl(submitter?.avatar ?? undefined);

    return (
      <Box className="relative" style={{ width: AVATAR_SLOT, height: AVATAR_SLOT }}>
        <Avatar size="xs" style={{ width: SUBMITTER_AVATAR_SIZE, height: SUBMITTER_AVATAR_SIZE }}>
          <AvatarImage
            source={uri ? { uri } : primum_default_avatar}
            alt={[submitter?.firstName, submitter?.lastName].filter(Boolean).join(' ')}
          />
        </Avatar>
        {submitterThreadCount > 1 ? (
          <Box
            aria-hidden
            className="absolute -bottom-0.5 right-0 h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-background-0 bg-background-100 px-1">
            <Text className="font-medium text-2xs leading-none text-typography-800">
              {submitterThreadCount}
            </Text>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box className="relative" style={{ width: AVATAR_SLOT, height: AVATAR_SLOT }}>
      {sentRows.map((row, index) => {
        const layout = getClusterLayout(index, sentRows.length);

        const uri = getAvatarUrl(row.avatar ?? undefined);

        return (
          <Avatar
            key={row.thread.conversationId}
            size="xs"
            className="absolute"
            style={{ left: layout.left, top: layout.top, width: layout.size, height: layout.size }}>
            <AvatarImage source={uri ? { uri } : primum_default_avatar} alt={row.name} />
          </Avatar>
        );
      })}
    </Box>
  );
}

/**
 * Two tones only. Unread is rust and semibold, matching the unread dot and the bold name, because
 * a new reply is the one state that asks for attention. Every other status, close requested,
 * closed and not sent, is the same gray and says what it is in words: separate hues for each were
 * tried and read as noise, and blue or green collided with the flat inbox's own badges. Each text
 * token clears AA on its own fill in both themes.
 */
const STATUS_BADGE_TONE: Record<QuestionGroupBadge['tone'], { shell: string; text: string }> = {
  unread: { shell: 'bg-tertiary-50 border-tertiary-300', text: 'font-semibold text-tertiary-700' },
  muted: { shell: 'bg-background-50 border-outline-300', text: 'text-typography-700' },
};

function StatusBadge({ label, tone }: { label: string; tone: QuestionGroupBadge['tone'] }) {
  const { shell, text } = STATUS_BADGE_TONE[tone];

  return (
    <Badge size="md" variant="outline" action="muted" className={twJoin('shrink-0', shell)}>
      <BadgeText size="sm" className={twJoin('normal-case', text)}>
        {label}
      </BadgeText>
    </Badge>
  );
}

function UnreadDot({ visible }: { visible: boolean }) {
  return (
    <Box
      testID={visible ? 'unread-indicator' : undefined}
      className={twJoin('h-2.5 w-2.5', visible && 'rounded-full bg-tertiary-400')}
    />
  );
}

/**
 * A submitter's own thread to Ask Anonymously is marked handled by staff rather than asked to be
 * closed by an expert, so its close request reads Handled, as the chat says it.
 */
function getThreadRowBadge(
  fromSubmitter: boolean,
  status: ReturnType<typeof getThreadStatus>
): QuestionGroupBadge | null {
  if (status === 'closed') {
    return { tone: 'muted', label: 'Closed' };
  }

  if (status === 'close_requested') {
    return { tone: 'muted', label: fromSubmitter ? 'Handled' : 'Close requested' };
  }

  return null;
}

/**
 * Line 2 for a row with nothing new to preview, so it says where it stands rather than staying
 * empty: a pick nobody has relayed to, or a sent thread the expert has not answered.
 */
function getRowStatusLine(notSent: boolean, thread: QuestionGroupRow['thread']) {
  if (notSent) {
    return NOT_RELAYED_LABEL;
  }

  return isThreadWaiting(thread) ? NO_REPLY_LABEL : null;
}

/**
 * A row mirrors the card header: the name and unread dot on the first line, the preview and the
 * status badge on the second, so a row's badge lines up under the header's.
 */
type QuestionGroupThreadRowProps = {
  row: QuestionGroupRow;
  isSelected: boolean;
  onOpen: (row: QuestionGroupRow) => void;
};

function QuestionGroupThreadRow({
  row,
  isSelected,
  onOpen,
  isLast,
}: QuestionGroupThreadRowProps & { isLast: boolean }) {
  const { thread, notSent, name: expertName } = row;

  const status = notSent ? 'open' : getThreadStatus(thread);

  const preview = getRowPreview(row);

  const secondLine = preview ?? getRowStatusLine(notSent, thread);

  const unread = Boolean(thread.hasUnread);

  // An unread reply stands out a step below the name: medium and dark, not a second semibold line.
  const previewTone = unread ? 'font-medium text-typography-900' : 'text-typography-700';

  const rowBadge = getThreadRowBadge(row.fromSubmitter, status);

  return (
    <Box className="relative pl-[48px]" testID={`question-group-thread-${thread.conversationId}`}>
      {isLast ? (
        <Box
          aria-hidden
          className="absolute left-[29.5px] top-0 h-[20px] w-[14.5px] rounded-bl-[14px] border-b border-l border-outline-300"
        />
      ) : (
        <Box
          aria-hidden
          className="absolute bottom-0 left-[29.5px] top-0 border-l border-outline-300"
        />
      )}
      <Pressable
        onPress={() => onOpen(row)}
        accessibilityRole="button"
        accessibilityLabel={`${expertName}${notSent ? ', not sent' : ''}${secondLine ? `, ${secondLine}` : ''}${unread ? ', unread' : ''}`}
        accessibilityState={{ selected: isSelected }}
        className={twJoin(
          'my-0.5 mr-2 rounded-[10px] py-[7px] pl-2.5 pr-2 web:transition-colors web:duration-150',
          isSelected
            ? 'bg-background-100'
            : 'data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100'
        )}>
        <VStack className="gap-0.5">
          <HStack className="items-center justify-between gap-2">
            <Text
              numberOfLines={1}
              size="md"
              className={twJoin(
                'min-w-0 flex-1',
                unread && 'font-semibold text-typography-900',
                !unread && isSelected && 'text-typography-900',
                !unread && !isSelected && 'text-typography-800'
              )}>
              {expertName}
            </Text>
            <UnreadDot visible={unread} />
          </HStack>
          {secondLine || rowBadge ? (
            <HStack className="items-center gap-1.5">
              <Text numberOfLines={1} size="sm" className={twJoin('min-w-0 flex-1', previewTone)}>
                {secondLine}
              </Text>
              {rowBadge ? <StatusBadge label={rowBadge.label} tone={rowBadge.tone} /> : null}
            </HStack>
          ) : null}
        </VStack>
      </Pressable>
    </Box>
  );
}

/**
 * Whether a one-line text is cut off with an ellipsis, so a hover card repeats the full text only
 * when there is more to see. Web only: it reads the rendered element's widths after layout, and
 * phones have no hover to show it on.
 */
function useIsTextTruncated() {
  const [element, setElement] = useState<HTMLElement | null>(null);

  const [isTruncated, setIsTruncated] = useState(false);

  // The app's Text renders a bare span on web, which never fires onLayout, so watch its size.
  const textRef = useCallback((node: RNText | null) => {
    setElement(isWeb ? (node as unknown as HTMLElement | null) : null);
  }, []);

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const measure = () => setIsTruncated(element.scrollWidth > element.clientWidth);

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [element]);

  return { textRef, isTruncated };
}

/**
 * The slot after the caret that holds the unread dot. It has a floor of the dot plus a gap, so a
 * long title gives up its own width first and the dot never lands on top of the caret.
 */
export const UNREAD_DOT_SLOT_CLASS = 'min-w-[18px] flex-1 items-end pl-2';

/** The caret draws 20px, so its tap area is widened without widening its hover tint over the title. */
export const QUESTION_GROUP_CARET_HIT_SLOP = getAccessibleHitSlop(20);

/**
 * Whether the inquiry group header carries the selected tint. An open card tints the selected row inside
 * it, a thread or the submitter's question, so one selection never shows two highlights; a
 * collapsed card has no row to carry it, so its header does.
 */
export function isHeaderTinted(
  isQuestionGroupSelected: boolean,
  expanded: boolean,
  isRowSelected: boolean
) {
  return isQuestionGroupSelected && !(expanded && isRowSelected);
}

/**
 * The header's state fill. The selected tint when it carries the selection, otherwise a hover tint
 * only while the card is collapsed: an open card's header is not the control, so a hover tint on
 * it would read as a second selection beside the selected row.
 */
export function getHeaderStateClass(tinted: boolean, expanded: boolean) {
  if (tinted) {
    return 'bg-background-100';
  }

  return expanded ? '' : 'web:hover:bg-background-50';
}

type QuestionGroupCardProps = {
  questionGroup: QuestionGroup;
  currentTab: string;
  /** True for the one card that carries the one-time tip. */
  showTip?: boolean;
};

/**
 * A staff group: the original inquiry's cancer type, submitter and case line over the expert relay
 * threads sent out from it. Tapping the row opens a conversation exactly as a flat row does, and
 * a separate caret after the title shows or hides the threads. The header's own press area is not
 * a button to assistive technology, so the caret and the title button never sit inside another
 * button there; a press lands on the innermost target only, so nothing toggles twice.
 *
 * Staff only. The title names the submitter, so callers render this only for the Primum group.
 */
export const QuestionGroupCard = memo(function QuestionGroupCard({
  questionGroup,
  currentTab,
  showTip = false,
}: QuestionGroupCardProps) {
  const { threads } = questionGroup;

  const params = useLocalSearchParams<{ id?: string }>();

  const selectedChatId = params.id === 'undefined' ? undefined : params.id;

  const openChat = useOpenInquiryChat(currentTab);

  const isExpandedInStore = useInboxQuestionGroupCardsStore(
    (state) => state.expandedByKey[questionGroup.key] ?? false
  );

  const setExpanded = useInboxQuestionGroupCardsStore((state) => state.setExpanded);

  const rows = useMemo(() => getQuestionGroupRows(questionGroup), [questionGroup]);

  const rowThreads = useMemo(() => rows.map((row) => row.thread), [rows]);

  const unreadSubmitterThreads = useMemo(
    () => getUnreadSubmitterThreads(questionGroup),
    [questionGroup]
  );

  /** The submitter's unread follow-up has no row, so the header keeps its dot while open. */
  const hasSubmitterUnread = unreadSubmitterThreads.length > 0;

  const hasCaret = rows.length > 1;

  const expanded = hasCaret && isExpandedInStore;

  const [tipStep, setTipStep] = useState<1 | 2>(1);

  const title = useMemo(
    () => buildQuestionGroupTitle(questionGroup.original, questionGroup.originalGroup),
    [questionGroup.original, questionGroup.originalGroup]
  );

  const heading = useMemo(
    () => buildQuestionGroupHeading(questionGroup.original),
    [questionGroup.original]
  );

  const caseLine = getQuestionGroupCaseLine(questionGroup.original, questionGroup.originalGroup);

  const { textRef: caseLineRef, isTruncated: isCaseLineTruncated } = useIsTextTruncated();

  const summary = summarizeQuestionGroup(rowThreads, {
    submitterSide: questionGroup.side === 'submitter',
  });

  const conversationIds = useMemo(
    () => getQuestionGroupConversationIds(questionGroup),
    [questionGroup]
  );

  const threadConversationIds = useMemo(
    () => threads.map((thread) => thread.conversationId),
    [threads]
  );

  const isQuestionGroupSelected =
    selectedChatId !== undefined && conversationIds.includes(selectedChatId);

  const isThreadSelected =
    selectedChatId !== undefined &&
    rowThreads.some((thread) => thread.conversationId === selectedChatId);

  const unreadCount = summary.unreadCount + unreadSubmitterThreads.length;

  const hasUnread = unreadCount > 0;

  const fanOutConversationIds = useMemo(
    () => questionGroup.originalGroup.map((inquiry) => inquiry.conversationId),
    [questionGroup.originalGroup]
  );

  const openRow = (row: QuestionGroupRow) =>
    openChat(row.thread, row.fromSubmitter ? fanOutConversationIds : threadConversationIds);

  const openQuestionGroup = () => {
    const target = getQuestionGroupOpenTarget(questionGroup);

    openChat(target.inquiry, target.siblingConversationIds);
  };

  const toggle = () => {
    setExpanded(questionGroup.key, !expanded);

    if (!expanded && tipStep === 1) {
      setTipStep(2);
    }
  };

  /**
   * An inquiry group with several threads expands on a tap and opens a conversation only from a thread row.
   * An inquiry group with one thread has nothing to expand, so a tap opens it, as a flat row does.
   * An open card whose submitter wrote an unread follow-up opens it from the header, since the
   * follow-up has no row of its own; the caret still closes the card.
   */
  const opensSubmitterThread = expanded && hasSubmitterUnread;

  const onHeaderPress = hasCaret && !opensSubmitterThread ? toggle : openQuestionGroup;

  const expertCount = rows.length;

  const accessibleName = [
    title,
    `${expertCount} ${expertCount === 1 ? 'expert' : 'experts'}`,
    hasUnread ? `${unreadCount} unread` : null,
  ]
    .filter(Boolean)
    .join(', ');

  const questionGroupNames = useMemo(
    () => formatQuestionGroupNames(threads, questionGroup.notSentExperts),
    [threads, questionGroup.notSentExperts]
  );

  const badge = getQuestionGroupBadge({ ...summary, unreadCount });

  return (
    <VStack className="px-3 py-1.5">
      <VStack
        testID={`question-group-card-${questionGroup.key}`}
        className="overflow-hidden rounded-[14px] border border-outline-100 bg-background-0">
        {/*
         * The whole header is one press target for the mouse and touch, so the padding and the
         * gaps between lines toggle the card too. It stays out of the accessibility tree: the
         * title button and the caret are what a keyboard or screen reader reaches.
         */}
        <Pressable
          accessible={false}
          tabIndex={-1}
          onPress={onHeaderPress}
          className={twJoin(
            'relative flex-row items-start gap-2 py-3.5 pl-2.5 pr-4 web:transition-colors web:duration-150',
            getHeaderStateClass(
              isHeaderTinted(isQuestionGroupSelected, expanded, isThreadSelected),
              expanded
            )
          )}>
          {expanded ? (
            <Box
              aria-hidden
              className="absolute bottom-0 left-[29.5px] top-[58px] border-l border-outline-300"
            />
          ) : null}
          <Box style={{ width: AVATAR_SLOT, height: AVATAR_SLOT }}>
            <QuestionGroupAvatars
              rows={rows}
              submitter={
                questionGroup.original.attributedSubmitter ?? questionGroup.original.author
              }
            />
          </Box>

          <VStack className="min-w-0 flex-1 gap-0.5">
            <HStack className="items-center">
              <Pressable
                onPress={onHeaderPress}
                accessibilityRole="button"
                accessibilityLabel={accessibleName}
                accessibilityHint={
                  hasCaret && !opensSubmitterThread ? 'Shows each expert' : 'Opens the conversation'
                }
                accessibilityState={{
                  selected: isQuestionGroupSelected,
                  expanded: hasCaret ? expanded : undefined,
                }}
                className="min-w-0 shrink">
                <Text
                  numberOfLines={1}
                  size="md"
                  className={
                    hasUnread && (!expanded || hasSubmitterUnread)
                      ? 'font-semibold text-typography-900'
                      : 'text-typography-800'
                  }>
                  {heading}
                </Text>
              </Pressable>
              {hasCaret ? (
                <Pressable
                  onPress={toggle}
                  accessibilityRole="button"
                  accessibilityLabel={expanded ? 'Hide experts' : 'Show experts'}
                  accessibilityState={{ expanded }}
                  testID={`question-group-caret-${questionGroup.key}`}
                  hitSlop={QUESTION_GROUP_CARET_HIT_SLOP}
                  className="h-5 w-5 shrink-0 items-center justify-center rounded-md data-[hover=true]:bg-background-100">
                  <Icon
                    as={expanded ? ChevronUp : ChevronDown}
                    size="sm"
                    className="text-typography-700"
                  />
                </Pressable>
              ) : null}
              <Box className={UNREAD_DOT_SLOT_CLASS}>
                <UnreadDot visible={hasUnread && (!expanded || hasSubmitterUnread)} />
              </Box>
            </HStack>

            {caseLine ? (
              <QuestionGroupHoverCard enabled={isCaseLineTruncated} text={caseLine}>
                {(triggerProps) => (
                  <Pressable
                    {...triggerProps}
                    // After the spread: the hover card's trigger brings its own press handler.
                    accessible={false}
                    tabIndex={-1}
                    onPress={onHeaderPress}
                    className="min-w-0 max-w-full self-start">
                    <Text
                      ref={caseLineRef}
                      numberOfLines={1}
                      size="sm"
                      className="text-typography-800">
                      {caseLine}
                    </Text>
                  </Pressable>
                )}
              </QuestionGroupHoverCard>
            ) : null}

            {/* Expanded, the rows name the experts and carry their own dots, so this line would
                hold only the badge and leave a gap under the header. */}
            {expanded ? null : (
              <HStack className="items-center gap-1.5">
                <Text
                  numberOfLines={1}
                  size="sm"
                  className={twJoin('min-w-0 flex-1', 'text-typography-700')}>
                  {questionGroupNames || NO_EXPERTS_LABEL}
                </Text>
                {badge ? <StatusBadge label={badge.label} tone={badge.tone} /> : null}
              </HStack>
            )}
          </VStack>
        </Pressable>

        {expanded ? (
          <VStack className="pb-2.5">
            {rows.map((row, index) => (
              <QuestionGroupThreadRow
                key={row.thread.conversationId}
                row={row}
                isLast={index === rows.length - 1}
                isSelected={selectedChatId === row.thread.conversationId}
                onOpen={openRow}
              />
            ))}
          </VStack>
        ) : null}
      </VStack>

      {showTip && hasCaret ? (
        <QuestionGroupTip
          step={tipStep}
          onNext={() => {
            setExpanded(questionGroup.key, true);

            setTipStep(2);
          }}
          onBack={() => {
            setExpanded(questionGroup.key, false);

            setTipStep(1);
          }}
        />
      ) : null}
    </VStack>
  );
});
